import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { LogIn, Shield, TrendingUp, Zap, Bot, AlertTriangle, Volume2, DollarSign, Settings, X } from 'lucide-react';

const LoginPage = () => {
  const { login, emergencyLogin } = useAuth();
  const [showInitialPopup, setShowInitialPopup] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [emergEmail, setEmergEmail] = useState('');
  const [emergCode, setEmergCode] = useState('');
  const [emergLoading, setEmergLoading] = useState(false);
  const [emergError, setEmergError] = useState('');
  const audioRef = useRef(null);
  const navigate = useNavigate();

  const handleEmergencyLogin = async () => {
    setEmergLoading(true);
    setEmergError('');
    try {
      const userData = await emergencyLogin(emergEmail, emergCode);
      navigate('/dashboard', { replace: true, state: { user: userData } });
    } catch (err) {
      setEmergError('Invalid credentials');
    } finally {
      setEmergLoading(false);
    }
  };

  // Preload audio
  useEffect(() => {
    const audio = new Audio('/disclaimer-sound.mp3');
    audio.preload = 'auto';
    audio.addEventListener('canplaythrough', () => setAudioLoaded(true));
    audioRef.current = audio;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleAcknowledge = async () => {
    // Play the disclaimer sound with user interaction (bypasses autoplay policy)
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.volume = 1.0;
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => console.log('Audio playing successfully'))
            .catch(e => console.error('Audio play failed:', e));
        }
      }
    } catch (e) {
      console.error('Audio error:', e);
    }
    
    // Show login page
    setDisclaimerAccepted(true);
  };

  // Disclaimer Splash Screen
  if (!disclaimerAccepted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        {/* Initial Real Money Warning Popup */}
        {showInitialPopup && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-xl matrix-card rounded-none border-2 border-yellow-500 bg-black shadow-[0_0_60px_rgba(255,200,0,0.4)]">
              <CardHeader className="text-center border-b border-yellow-500/50 pb-4 relative">
                <button 
                  onClick={() => setShowInitialPopup(false)}
                  className="absolute top-4 right-4 text-[#008F11] hover:text-[#00FF41] transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex justify-center mb-3">
                  <div className="p-4 rounded-full bg-yellow-500/20 border-2 border-yellow-500 animate-pulse">
                    <DollarSign className="h-10 w-10 text-yellow-500" />
                  </div>
                </div>
                <CardTitle className="text-2xl font-bold text-yellow-500 tracking-wider"
                    style={{ fontFamily: 'Courier New, monospace', textShadow: '0 0 10px rgba(255,200,0,0.5)' }}>
                  REAL MONEY TRADING
                </CardTitle>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="space-y-4 text-center">
                  <p className="text-[#00FF41] text-lg leading-relaxed">
                    This site has the ability to <span className="text-cyan-400 font-bold">simulate</span> bitcoin trading, 
                    but it also has the ability to trade <span className="text-yellow-500 font-bold">REAL MONEY</span> in 
                    <span className="text-red-500 font-bold"> LIVE MODE</span>.
                  </p>
                  
                  <div className="bg-[#001500] border border-[#003B00] p-4 rounded">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Settings className="h-5 w-5 text-cyan-400" />
                      <span className="text-cyan-400 font-bold">SETUP REQUIRED FOR LIVE TRADING</span>
                    </div>
                    <p className="text-[#008F11] text-sm">
                      If you want to trade for real, you will need to generate <span className="text-[#00FF41] font-bold">API keys</span> for your exchange. 
                      You set that up in <span className="text-yellow-500 font-bold">CONFIG</span>.
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-center gap-4 pt-2">
                    <div className="text-center">
                      <div className="text-cyan-400 text-xs mb-1">SIMULATION</div>
                      <div className="text-[#00FF41] text-sm">No API keys needed</div>
                    </div>
                    <div className="text-[#003B00]">|</div>
                    <div className="text-center">
                      <div className="text-yellow-500 text-xs mb-1">LIVE MODE</div>
                      <div className="text-[#00FF41] text-sm">Requires exchange API keys</div>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={() => setShowInitialPopup(false)}
                  className="w-full h-12 text-lg font-bold tracking-wider rounded-none bg-[#003B00] hover:bg-[#005500] text-[#00FF41] transition-all duration-300 border border-[#00FF41] mt-6"
                  data-testid="understand-popup-btn"
                >
                  I UNDERSTAND
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        
        <Card className="w-full max-w-lg matrix-card rounded-none border-2 border-[#00FF41] bg-black/95 shadow-[0_0_50px_rgba(0,255,65,0.3)]">
          <CardHeader className="text-center border-b border-[#003B00] pb-6">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/bitbot-logo.png" 
                alt="BitBot" 
                className="w-auto"
                style={{ height: '225px' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <CardTitle className="text-3xl font-bold text-[#00FF41] tracking-wider"
                style={{ fontFamily: 'Courier New, monospace', textShadow: '0 0 15px #00FF41' }}>
              BITBOT V3.9.32 [LIVE]
            </CardTitle>
            <CardDescription className="text-[#008F11] text-sm tracking-widest mt-2">
              AUTOMATED CRYPTOCURRENCY TRADING
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-yellow-500/20 border border-yellow-500">
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
            
            {/* Disclaimer Text */}
            <div className="bg-[#001500] border border-[#003B00] p-4 mb-6">
              <h3 className="text-yellow-500 font-bold text-sm mb-3 tracking-wider text-center">
                ⚠️ IMPORTANT DISCLAIMER ⚠️
              </h3>
              <div className="text-[#00FF41] text-sm leading-relaxed space-y-3">
                <p>
                  This BOT can <span className="text-yellow-500 font-bold">simulate</span> or trade <span className="text-yellow-500 font-bold">actual coins and currency</span>.
                </p>
                <p>
                  <span className="text-white font-bold">Important:</span> The bot continues running even if you close the browser (until you click STOP — there is no time limit). This is intentional for a trading bot.
                </p>
                <p className="text-red-500 font-bold text-center pt-2 border-t border-[#003B00]">
                  Any loss of capital is AT YOUR OWN RISK
                </p>
              </div>
            </div>
            
            {/* Acknowledge Button */}
            <Button
              onClick={handleAcknowledge}
              className="w-full h-14 text-lg font-bold tracking-wider rounded-none bg-yellow-600 hover:bg-yellow-500 text-black transition-all duration-300 border-2 border-yellow-500"
              data-testid="acknowledge-btn"
            >
              <Volume2 className="h-5 w-5 mr-2" />
              I UNDERSTAND & ACKNOWLEDGE
            </Button>
            
            <p className="text-[#003B00] text-xs text-center mt-4">
              By clicking above, you accept full responsibility for any trading outcomes
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Login Page (after disclaimer accepted)
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/bitbot-logo.png" 
              alt="BitBot" 
              className="w-auto"
              style={{ height: '421px' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <h1 className="text-4xl font-bold text-white tracking-wider mb-2" 
              style={{ fontFamily: 'Courier New, monospace', textShadow: '0 0 20px rgba(255,255,255,0.8), 0 0 40px rgba(255,255,255,0.4)' }}>
            BITBOT V3.9.32 [LIVE]
          </h1>
          <p className="text-white text-sm tracking-widest"
             style={{ textShadow: '0 0 15px rgba(255,255,255,0.6), 0 0 30px rgba(255,255,255,0.3)' }}>
            AUTOMATED BITCOIN TRADING
          </p>
        </div>

        {/* Login Card */}
        <Card className="matrix-card rounded-none border border-[#003B00] bg-black/90" data-testid="login-card">
          <CardHeader className="border-b border-[#003B00] pb-4">
            <CardTitle className="text-[#00FF41] text-center text-lg tracking-widest flex items-center justify-center gap-2">
              <Shield className="h-5 w-5" />
              SECURE ACCESS
            </CardTitle>
            <CardDescription className="text-[#008F11] text-center text-xs">
              Sign in to access your trading dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {/* Features */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-[#008F11] text-sm">
                <Bot className="h-4 w-4 text-[#00FF41]" />
                <span>Automated RSI-based trading</span>
              </div>
              <div className="flex items-center gap-3 text-[#008F11] text-sm">
                <TrendingUp className="h-4 w-4 text-[#00FF41]" />
                <span>Multi-coin support (BTC, ETH, SOL)</span>
              </div>
              <div className="flex items-center gap-3 text-[#008F11] text-sm">
                <Zap className="h-4 w-4 text-[#00FF41]" />
                <span>Real-time Coinbase integration</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <Button
              onClick={login}
              className="w-full h-12 matrix-btn rounded-none border-[#00FF41] text-[#00FF41] hover:bg-[#00FF41] hover:text-black transition-all duration-300"
              data-testid="google-login-btn"
            >
              <LogIn className="h-5 w-5 mr-2" />
              SIGN IN WITH GOOGLE
            </Button>

            {/* Admin Direct Access */}
            {!showEmergency ? (
              <button
                data-testid="show-direct-login-btn"
                onClick={() => setShowEmergency(true)}
                className="w-full text-center text-[#003B00] text-xs mt-3 hover:text-[#008F11] transition-colors cursor-pointer"
              >
                Admin Direct Access
              </button>
            ) : (
              <form 
                method="POST" 
                action={`${process.env.REACT_APP_BACKEND_URL}/api/v2/auth/direct-login`}
                className="mt-3 space-y-2" 
                data-testid="direct-login-form"
              >
                <input
                  data-testid="direct-login-email"
                  type="email"
                  name="email"
                  placeholder="Admin email"
                  className="w-full px-3 py-2 bg-black border border-[#003B00] text-[#00FF41] text-sm font-mono rounded-none focus:border-[#00FF41] focus:outline-none"
                />
                <input
                  data-testid="direct-login-code"
                  type="password"
                  name="access_code"
                  placeholder="Access code"
                  className="w-full px-3 py-2 bg-black border border-[#003B00] text-[#00FF41] text-sm font-mono rounded-none focus:border-[#00FF41] focus:outline-none"
                />
                <Button
                  type="submit"
                  data-testid="direct-login-submit"
                  className="w-full h-10 rounded-none border-yellow-600 bg-yellow-600/20 text-yellow-500 hover:bg-yellow-600 hover:text-black transition-all duration-300"
                >
                  DIRECT LOGIN
                </Button>
              </form>
            )}

            <p className="text-[#008F11] text-xs text-center mt-4 opacity-70">
              Your portfolio data is stored securely and only accessible to you
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 space-y-3">
          <p className="text-[#003B00] text-xs">
            SYSTEM READY • {new Date().toLocaleTimeString('en-US', { hour12: false })}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-[10px] text-gray-600">
            <a href="/about" className="hover:text-[#00FF41] transition-colors">About</a>
            <span className="text-gray-800">|</span>
            <a href="/faq" className="hover:text-[#00FF41] transition-colors">FAQ</a>
            <span className="text-gray-800">|</span>
            <a href="/contact" className="hover:text-[#00FF41] transition-colors">Contact</a>
            <span className="text-gray-800">|</span>
            <a href="/privacy" className="hover:text-[#00FF41] transition-colors">Privacy</a>
            <span className="text-gray-800">|</span>
            <a href="/terms" className="hover:text-[#00FF41] transition-colors">Terms</a>
            <span className="text-gray-800">|</span>
            <a href="/disclaimer" className="hover:text-[#00FF41] transition-colors">Disclaimer</a>
            <span className="text-gray-800">|</span>
            <a href="/cookies" className="hover:text-[#00FF41] transition-colors">Cookies</a>
            <span className="text-gray-800">|</span>
            <a href="/changelog" className="hover:text-[#00FF41] transition-colors">Changelog</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
